import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  message,
  Popconfirm,
  Select,
  Spin,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useState } from 'react';

import type { ClassOption, ClubOption, MemberRow, MembershipInvoice, StudentOption } from './types';
import { formatMNT, formatPeriod, queryErrorMessage } from './types';

const { Text } = Typography;

interface VoucherOption {
  id: number;
  name: string;
  percentage: number;
  // null = unlimited.
  uses_left: number | null;
}

// One Club month a Join bills now (CONTEXT.md: Club invoice).
interface PricingLine {
  period_start: string;
  period_end: string;
  sessions_billed: number;
  sessions_total: number;
  full_amount: number;
  discount_amount: number;
  amount: number;
}

interface PricingInfo {
  totalPrice: number;
  discountAmount: number;
  discountPercentage: number;
  voucherMonths: number;
  vouchers: VoucherOption[];
  studentBalance: number;
  sufficientBalance: boolean;
  startDate: string;
  // The student's Outstanding payments in every club (CONTEXT.md: Join).
  outstandingAmount: number;
  lines: PricingLine[];
}

interface JoinResult {
  already_joined: boolean;
  paid: boolean;
  needs_confirmation: boolean;
  outstanding_amount: number;
}

export default function MembershipDrawer({
  mode,
  club,
  members,
  onClose,
  onDone,
}: {
  mode: 'join' | 'cancel';
  club: ClubOption;
  members: MemberRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { runQuery } = useScreen();
  const cancelClassNames = Array.from(
    new Set(members.map((r) => r.class_name).filter((c): c is string => !!c)),
  ).sort((a, b) => a.localeCompare(b, 'mn'));

  const [selectedClassKey, setSelectedClassKey] = useState<string | number>();
  const [joinClasses, setJoinClasses] = useState<ClassOption[]>([]);
  const [joinClassesLoading, setJoinClassesLoading] = useState(false);
  const [joinStudents, setJoinStudents] = useState<StudentOption[]>([]);
  const [joinStudentsLoading, setJoinStudentsLoading] = useState(false);

  const [selectedTargetId, setSelectedTargetId] = useState<number>();
  // Start date (CONTEXT.md): today, or an earlier day for a student who came
  // before being joined — each begun Club month is then billed at once.
  const [startDate, setStartDate] = useState<Dayjs>(() => dayjs());
  // An admin may Join a student with Outstanding payments, once confirmed.
  const [confirmOutstanding, setConfirmOutstanding] = useState(false);
  // Nothing is pre-selected (CONTEXT.md: Join) — the admin opts in.
  const [studentVoucherId, setStudentVoucherId] = useState<number | null>(null);
  const [pricing, setPricing] = useState<PricingInfo | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // Cancel: the chosen Membership's invoices, and which paid ones to refund.
  const [invoices, setInvoices] = useState<MembershipInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [refundIds, setRefundIds] = useState<number[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'join') return;
    setJoinClassesLoading(true);
    runQuery<ClassOption>('club_class_options')
      .then(setJoinClasses)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setJoinClassesLoading(false));
  }, [mode, runQuery]);

  useEffect(() => {
    setSelectedTargetId(undefined);
    setPricing(null);
    setError(null);
    if (mode !== 'join' || !selectedClassKey) {
      setJoinStudents([]);
      return;
    }
    setJoinStudentsLoading(true);
    runQuery<StudentOption>('club_class_students', { classId: selectedClassKey })
      .then(setJoinStudents)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setJoinStudentsLoading(false));
  }, [mode, selectedClassKey, runQuery]);

  // A voucher belongs to one student: a new student starts with none.
  useEffect(() => {
    setStudentVoucherId(null);
    setConfirmOutstanding(false);
  }, [selectedTargetId]);

  useEffect(() => {
    setError(null);
    if (mode !== 'join' || !selectedTargetId) {
      setPricing(null);
      return;
    }
    setPricingLoading(true);
    runQuery<PricingInfo>('club_pricing', {
      studentId: selectedTargetId,
      clubId: club.id,
      studentVoucherId,
      startDate: startDate.format('YYYY-MM-DD'),
    })
      .then(([row]) => setPricing(row ?? null))
      .catch((e) => {
        setPricing(null);
        setError(queryErrorMessage(e));
      })
      .finally(() => setPricingLoading(false));
  }, [mode, selectedTargetId, startDate, studentVoucherId, club.id, runQuery]);

  useEffect(() => {
    setRefundIds([]);
    setError(null);
    if (mode !== 'cancel' || !selectedTargetId) {
      setInvoices([]);
      return;
    }
    setInvoicesLoading(true);
    runQuery<MembershipInvoice>('club_membership_invoices', { clubStudentId: selectedTargetId })
      .then(setInvoices)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setInvoicesLoading(false));
  }, [mode, selectedTargetId, runQuery]);

  const handleSubmit = () => {
    if (!selectedTargetId) return;
    setSubmitting(true);
    setError(null);

    const request =
      mode === 'join'
        ? runQuery<JoinResult>('club_join', {
            studentId: selectedTargetId,
            clubId: club.id,
            studentVoucherId,
            startDate: startDate.format('YYYY-MM-DD'),
            confirmOutstanding,
          }).then(([row]) => {
            if (row?.needs_confirmation) {
              throw {
                error: `Сурагч ${formatMNT(row.outstanding_amount)} хугацаа хэтэрсэн төлбөртэй байна. Баталгаажуулаад дахин оролдоно уу.`,
              };
            }
            if (row?.already_joined) message.info('Аль хэдийн гишүүн байна.');
            else if (row?.paid) message.success('Гишүүнчлэл үүсч, төлбөр төлөгдлөө.');
            else message.warning('Гишүүнчлэл үүслээ. Төлбөр төлөгдөөгүй үлдлээ.');
          })
        : runQuery('club_cancel', {
            clubStudentId: selectedTargetId,
            refundInvoiceIds: refundIds,
          }).then(() => {
            message.success('Гишүүнчлэл цуцлагдлаа.');
          });

    request
      .then(onDone)
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setSubmitting(false));
  };

  const title = mode === 'join' ? 'Шинэ гишүүнчлэл үүсгэх' : 'Гишүүнчлэл цуцлах';

  // Хоосон бол клуб бүх ангид нээлттэй — эс тэгвээс зөвхөн зөвшөөрөгдсөн
  // анги жилийн ангиудыг л сонголтод харуулна.
  const allowedClassYearIds = club.class_year_ids;
  const joinClassesForClub =
    allowedClassYearIds.length === 0
      ? joinClasses
      : joinClasses.filter(
          (c) => c.class_year_id != null && allowedClassYearIds.includes(c.class_year_id),
        );

  const classOptions: { value: string | number; label: string }[] =
    mode === 'join'
      ? joinClassesForClub.map((c) => ({
          value: c.id,
          label: c.class_number ? `${c.name} (${c.class_number}-р анги)` : c.name,
        }))
      : cancelClassNames.map((name) => ({ value: name, label: name }));

  const studentOptions =
    mode === 'join'
      ? joinStudents.map((s) => ({ value: s.id, label: `${s.full_name} (${s.code})` }))
      : members
          .filter((r) => !selectedClassKey || r.class_name === selectedClassKey)
          .map((r) => ({ value: r.id, label: r.student_name }));

  // Join no longer refuses a short balance: the invoice stays outstanding.
  const insufficientBalance = mode === 'join' && pricing != null && !pricing.sufficientBalance;
  const hasOutstanding = mode === 'join' && (pricing?.outstandingAmount ?? 0) > 0;
  const outstandingInvoices = invoices.filter((inv) => inv.status === 'PENDING');
  const paidInvoices = invoices.filter((inv) => inv.status === 'PAID');
  const refundTotal = paidInvoices
    .filter((inv) => refundIds.includes(inv.id))
    .reduce((sum, inv) => sum + inv.refundable_amount, 0);
  // A Cancel keeps a begun Club month's Sessions up to today owed; the rest is voided.
  const keptTotal = outstandingInvoices.reduce((sum, inv) => sum + inv.cancel_keep_amount, 0);
  const voidedCount = outstandingInvoices.filter((inv) => inv.cancel_keep_amount === 0).length;

  const selectedLabel = studentOptions.find((o) => o.value === selectedTargetId)?.label;
  const who = selectedLabel ?? 'Сонгосон сурагч';
  const confirmDescription =
    mode === 'join'
      ? `${who}-г "${club.name}" дугуйланд ${startDate.format('YYYY-MM-DD')}-ноос бүртгэнэ.${
          pricing
            ? insufficientBalance
              ? ` ${formatMNT(pricing.totalPrice)} төлөгдөөгүй үлдэнэ.`
              : ` ${formatMNT(pricing.totalPrice)}-г картын үлдэгдлээс хасна.`
            : ''
        }`
      : `${who}-ийн гишүүнчлэл цуцлагдана.${
          voidedCount ? ` ${voidedCount} төлөгдөөгүй нэхэмжлэл хүчингүй болно.` : ''
        }${keptTotal ? ` ${formatMNT(keptTotal)} төлөгдөөгүй хэвээр үлдэнэ.` : ''}${refundTotal ? ` ${formatMNT(refundTotal)} картын үлдэгдэл рүү буцна.` : ' Мөнгө буцаахгүй.'} Энэ үйлдлийг буцаах боломжгүй.`;

  const period = (inv: MembershipInvoice) => formatPeriod(inv);

  return (
    <Drawer title={title} open onClose={onClose} width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Дугуйлан
          </Text>
          <div style={{ fontWeight: 600 }}>{club.name}</div>
        </div>

        <Select
          placeholder="Анги сонгох"
          loading={mode === 'join' ? joinClassesLoading : false}
          value={selectedClassKey}
          onChange={(value) => setSelectedClassKey(value)}
          allowClear
          onClear={() => setSelectedClassKey(undefined)}
          options={classOptions}
        />

        <Select
          placeholder="Сурагч сонгох"
          loading={mode === 'join' ? joinStudentsLoading : false}
          disabled={!selectedClassKey}
          value={selectedTargetId}
          onChange={(value) => setSelectedTargetId(value)}
          allowClear
          onClear={() => setSelectedTargetId(undefined)}
          notFoundContent={
            selectedClassKey && mode === 'cancel' ? 'Энэ ангид гишүүн алга' : undefined
          }
          options={studentOptions}
        />

        {mode === 'join' && selectedTargetId && (
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
              Элссэн огноо
            </Text>
            <DatePicker
              style={{ width: '100%' }}
              value={startDate}
              allowClear={false}
              onChange={(value) => value && setStartDate(value)}
              disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            />
            {!startDate.isSame(dayjs(), 'day') && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Өмнөх огноо: тэр өдрөөс хойш эхэлсэн сар бүрийг нэг дор нэхэмжилнэ.
              </Text>
            )}
          </div>
        )}

        {mode === 'join' && selectedTargetId && (pricing?.vouchers.length ?? 0) > 0 && (
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
              Ваучер
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Ваучер ашиглахгүй"
              value={studentVoucherId ?? undefined}
              onChange={(value) => setStudentVoucherId(value ?? null)}
              allowClear
              onClear={() => setStudentVoucherId(null)}
              options={pricing?.vouchers.map((v) => ({
                value: v.id,
                label: `${v.name} — ${v.percentage}% (${
                  v.uses_left == null ? 'хязгааргүй' : `${v.uses_left} сар үлдсэн`
                })`,
              }))}
            />
          </div>
        )}

        {mode === 'join' && selectedTargetId && (
          <>
            {pricingLoading && <Spin />}
            {!pricingLoading && pricing && (
              <div
                style={{
                  borderRadius: 8,
                  padding: 12,
                  background: pricing.sufficientBalance ? '#f6ffed' : '#fff7e6',
                  border: `1px solid ${pricing.sufficientBalance ? '#b7eb8f' : '#ffd591'}`,
                }}
              >
                {pricing.lines.length === 0 && (
                  <p style={{ margin: '0 0 8px' }}>
                    Одоогоор нэхэмжлэх сар алга — эхний сар эхлэхээс өмнө автоматаар нэхэмжилнэ.
                  </p>
                )}
                {pricing.lines.map((l) => (
                  <div
                    key={l.period_start}
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}
                  >
                    <span>{formatPeriod(l)}</span>
                    <span>
                      {l.discount_amount > 0 && (
                        <Text delete type="secondary" style={{ marginRight: 6 }}>
                          {formatMNT(l.full_amount)}
                        </Text>
                      )}
                      {formatMNT(l.amount)}
                    </span>
                  </div>
                ))}
                {pricing.discountAmount > 0 && (
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}
                  >
                    <Text type="secondary">
                      Ваучер {pricing.discountPercentage}% ({pricing.voucherMonths} сар)
                    </Text>
                    <Text type="secondary">−{formatMNT(pricing.discountAmount)}</Text>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Төлбөр</span>
                  <strong>{formatMNT(pricing.totalPrice)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span>Картын үлдэгдэл</span>
                  <strong>{formatMNT(pricing.studentBalance)}</strong>
                </div>
                {!pricing.sufficientBalance && (
                  <p style={{ margin: '8px 0 0', color: '#d46b08', fontSize: 12 }}>
                    Картын үлдэгдэл хүрэлцэхгүй — гишүүнчлэл үүсч, төлбөр төлөгдөөгүй үлдэнэ.
                  </p>
                )}
              </div>
            )}
            {!pricingLoading && hasOutstanding && pricing && (
              <Alert
                type="warning"
                showIcon
                message={`Сурагч ${formatMNT(pricing.outstandingAmount)} хугацаа хэтэрсэн төлбөртэй байна.`}
                description={
                  <Checkbox
                    checked={confirmOutstanding}
                    onChange={(e) => setConfirmOutstanding(e.target.checked)}
                  >
                    Мэдэж байна, бүртгэе
                  </Checkbox>
                }
              />
            )}
          </>
        )}

        {mode === 'cancel' && selectedTargetId && (
          <>
            {invoicesLoading && <Spin />}
            {!invoicesLoading && outstandingInvoices.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Төлөгдөөгүй нэхэмжлэл
                </Text>
                {outstandingInvoices.map((inv) => (
                  <div
                    key={inv.id}
                    style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}
                  >
                    <span>{period(inv)}</span>
                    <span>
                      {inv.cancel_keep_amount === 0 ? (
                        <>
                          {formatMNT(inv.total_price)} <Tag color="orange">хүчингүй болно</Tag>
                        </>
                      ) : inv.cancel_keep_amount < inv.total_price ? (
                        <>
                          <Text delete type="secondary">
                            {formatMNT(inv.total_price)}
                          </Text>{' '}
                          {formatMNT(inv.cancel_keep_amount)} <Tag color="gold">буурна</Tag>
                        </>
                      ) : (
                        <>
                          {formatMNT(inv.total_price)} <Tag color="gold">хэвээр үлдэнэ</Tag>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {!invoicesLoading && paidInvoices.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Төлөгдсөн нэхэмжлэл — буцаах бол сонгоно уу
                </Text>
                {paidInvoices.map((inv) => (
                  <div key={inv.id} style={{ marginTop: 4 }}>
                    <Checkbox
                      checked={refundIds.includes(inv.id)}
                      disabled={inv.refundable_amount === 0}
                      onChange={(e) =>
                        setRefundIds((ids) =>
                          e.target.checked ? [...ids, inv.id] : ids.filter((id) => id !== inv.id),
                        )
                      }
                    >
                      {period(inv)} ·{' '}
                      {inv.refundable_amount === 0
                        ? `${formatMNT(inv.total_price)} — буцаах өдөр үлдээгүй`
                        : inv.refundable_amount < inv.total_price
                          ? `${formatMNT(inv.total_price)}-ээс ${formatMNT(inv.refundable_amount)} буцаах`
                          : `${formatMNT(inv.total_price)} буцаах`}
                    </Checkbox>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {error && <Alert type="error" showIcon message={error} />}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button onClick={onClose} disabled={submitting}>
            Болих
          </Button>
          <Popconfirm
            title={mode === 'join' ? 'Гишүүнчлэл үүсгэх үү?' : 'Гишүүнчлэл цуцлах уу?'}
            description={confirmDescription}
            okText={mode === 'join' ? 'Бүртгэх' : 'Цуцлах'}
            okButtonProps={{ danger: mode === 'cancel' }}
            cancelText="Болих"
            onConfirm={handleSubmit}
            disabled={!selectedTargetId}
          >
            <Button
              type="primary"
              danger={mode === 'cancel'}
              disabled={
                !selectedTargetId ||
                submitting ||
                invoicesLoading ||
                pricingLoading ||
                (hasOutstanding && !confirmOutstanding)
              }
              loading={submitting}
            >
              {mode === 'join' ? 'Гишүүнчлэл үүсгэх' : 'Цуцлах'}
            </Button>
          </Popconfirm>
        </div>
      </div>
    </Drawer>
  );
}
